/** biome-ignore-all lint/suspicious/noExplicitAny: temporary */
// S-expression parser for the dashboard engine. Spec: SEXPR_ARCHITECTURE.md §4.
// Single entry point `parse(src)` returns one `AstNode` (the spec's
// `Program = Expression` rule). All syntactic checks are parse-time:
// list-head-must-be-a-symbol, positional-before-kwarg, no bare keywords,
// reserved words, and so on. Errors are thrown as `ParseError` carrying one
// `Diagnostic`.
//
// Design notes:
// - Immutable lex state (`Lex`) — every consumed character produces a new
//   state. Line/col/offset are tracked together so every token's `SourcePos`
//   is exact.
// - Word boundaries split tokens by class: whitespace, delimiters `()[]`,
//   string opener `"`, comment opener `;`, and keyword opener `:`.
// - Number vs symbol disambiguation matches the spec's "lookahead" rule: a
//   word that starts with a digit, or with `-` followed by a digit, is
//   classified as a number and validated against `NUMBER_RE`; otherwise it
//   is classified as a symbol and validated against `SYMBOL_RE`. A word that
//   fails its expected class is a diagnostic, not a silently-accepted
//   symbol — so `1abc`, `1-2`, and `sales.revenue` all surface clear errors.

import type { AstArg, AstNode, ListNode, SourcePos, VectorNode } from './ast.ts';
import { NO_POS } from './ast.ts';
import {
  BareKeywordError,
  EmptyKeywordError,
  EmptyListError,
  InvalidNumberError,
  InvalidTokenError,
  KwargNoValueError,
  ListHeadNotSymbolError,
  ReservedWordError,
  StrayRbracketError,
  StrayRparenError,
  UnclosedListError,
  UnclosedVectorError,
  UnexpectedEofError,
  UnknownEscapeError,
  UnterminatedStringError,
} from './errors.ts';

// Tokens (internal — never exposed)

type Tok =
  | { readonly t: 'lparen' | 'rparen' | 'lbracket' | 'rbracket'; readonly pos: SourcePos }
  | { readonly t: 'number'; readonly value: number; readonly pos: SourcePos }
  | { readonly t: 'string'; readonly value: string; readonly pos: SourcePos }
  | { readonly t: 'boolean'; readonly value: boolean; readonly pos: SourcePos }
  | { readonly t: 'null'; readonly pos: SourcePos }
  | { readonly t: 'symbol'; readonly name: string; readonly pos: SourcePos }
  | { readonly t: 'keyword'; readonly name: string; readonly pos: SourcePos };

// Character predicates and grammar regexes

const isWs = (c: string | undefined): boolean => c === ' ' || c === '\t' || c === '\n' || c === '\r';

const isDelim = (c: string | undefined): boolean => c === '(' || c === ')' || c === '[' || c === ']';

const isDigit = (c: string | undefined): boolean => c !== undefined && c >= '0' && c <= '9';

// `SymbolStart` and `SymbolCont` from §4.3 EBNF. The text in §4.2 lists a
// narrower SymbolCont (no operator chars); we follow the EBNF, which the
// §4.2 examples (`>=`, `&&`-style) implicitly require. `$` is admitted as
// an extension so engine-synthesised columns (notably the `$instanceId`
// identity column every `query-knowledge-graph` result carries) are
// referenceable by name in `(col …)` / `:where` / `:sort` — the producer
// and the surface grammar must agree on the identifier alphabet.
const SYMBOL_START_CHARS = '_$+-*/<>=!?&|';
const isSymbolStart = (c: string): boolean =>
  (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || SYMBOL_START_CHARS.includes(c);
const isSymbolCont = (c: string): boolean => isSymbolStart(c) || isDigit(c);

// A word boundary terminates a SYMBOL/NUMBER/BOOLEAN/NULL token. Everything
// else (letters, digits, operator chars, `_`, `-`, `.`, `/`, etc.) is read
// into the word and then classified post-scan.
const isWordBoundary = (c: string | undefined): boolean =>
  c === undefined || isWs(c) || isDelim(c) || c === '"' || c === ';' || c === ':';

// NUMBER per §4.3 EBNF: optional leading `-`, then digits with optional `_`
// separators, optional fractional part (digits + `_`), optional exponent.
const NUMBER_RE = /^-?\d[\d_]*(?:\.\d[\d_]*)?(?:[eE][+-]?\d+)?$/;

// SYMBOL per §4.3 EBNF: SymbolStart followed by zero or more SymbolCont.
const SYMBOL_RE = /^[A-Za-z_$+\-*/<>=!?&|][A-Za-z0-9_$+\-*/<>=!?&|]*$/;

// `\uXXXX` escapes in strings: exactly four hex digits.
const UNICODE_HEX_RE = /^[0-9A-Fa-f]{4}$/;

const RESERVED_WORDS: ReadonlySet<string> = new Set(['true', 'false', 'null']);

const STRING_ESCAPES: Readonly<Record<string, string>> = {
  n: '\n',
  t: '\t',
  r: '\r',
  '"': '"',
  '\\': '\\',
};

// Single source of truth for paren-like tokens: kind → recognising char.
// Both the lexer's char→kind table (`SINGLE_CHAR_TOKS`) and the
// diagnostics-facing describers (`TOK_DESCRIBERS`) derive from this; adding
// a new paren-like kind needs an update only here (plus the `Tok` union).
const PAREN_CHARS = {
  lparen: '(',
  rparen: ')',
  lbracket: '[',
  rbracket: ']',
} as const satisfies Partial<Record<Tok['t'], string>>;

const SINGLE_CHAR_TOKS: Readonly<Record<string, Tok['t']>> = Object.fromEntries(
  Object.entries(PAREN_CHARS).map(([kind, char]) => [char, kind as Tok['t']] as const),
);

// Lexer

type Lex = {
  readonly src: string;
  readonly i: number; //    0-based byte offset
  readonly line: number; // 1-based line number
  readonly col: number; //  1-based column number
};

const initLex = (src: string): Lex => ({ src, i: 0, line: 1, col: 1 });

// Advance `n` characters; recompute line/col by inspecting consumed text.
const advance = (lex: Lex, n: number): Lex => {
  if (n <= 0) {
    return lex;
  }
  const consumed = lex.src.slice(lex.i, lex.i + n);
  const newlines = (consumed.match(/\n/g) ?? []).length;
  const lastNl = consumed.lastIndexOf('\n');
  return {
    src: lex.src,
    i: lex.i + n,
    line: lex.line + newlines,
    col: lastNl === -1 ? lex.col + n : n - lastNl,
  };
};

const posAt = (lex: Lex, length: number): SourcePos => ({
  line: lex.line,
  col: lex.col,
  offset: lex.i,
  length,
});

const posFrom = (start: Lex, end: Lex): SourcePos => ({
  line: start.line,
  col: start.col,
  offset: start.i,
  length: end.i - start.i,
});

type Read = { readonly tok: Tok | null; readonly next: Lex };

const skipComment = (lex: Lex): Lex => {
  const nl = lex.src.indexOf('\n', lex.i);
  const end = nl === -1 ? lex.src.length : nl;
  return advance(lex, end - lex.i);
};

const readString = (start: Lex): Read => {
  // `start` points at the opening `"`.
  const consume = (cur: Lex, value: string): Read => {
    const c = cur.src[cur.i];
    if (c === undefined) {
      throw new UnterminatedStringError(posFrom(start, cur));
    }
    if (c === '"') {
      const next = advance(cur, 1);
      return { tok: { t: 'string', value, pos: posFrom(start, next) }, next };
    }
    if (c === '\\') {
      const esc = cur.src[cur.i + 1];
      if (esc === undefined) {
        throw new UnterminatedStringError(posFrom(start, cur));
      }
      if (esc === 'u') {
        const hex = cur.src.slice(cur.i + 2, cur.i + 6);
        if (!UNICODE_HEX_RE.test(hex)) {
          throw new UnknownEscapeError(`u${hex}`, posAt(cur, Math.min(6, cur.src.length - cur.i)));
        }
        return consume(advance(cur, 6), value + String.fromCharCode(Number.parseInt(hex, 16)));
      }
      const decoded = STRING_ESCAPES[esc];
      if (decoded === undefined) {
        throw new UnknownEscapeError(esc, posAt(cur, 2));
      }
      return consume(advance(cur, 2), value + decoded);
    }
    return consume(advance(cur, 1), value + c);
  };
  return consume(advance(start, 1), '');
};

const readKeyword = (start: Lex): Read => {
  // `start` points at `:`.
  const afterColon = advance(start, 1);
  const first = afterColon.src[afterColon.i];
  if (first === undefined || !isSymbolStart(first)) {
    throw new EmptyKeywordError(posFrom(start, afterColon));
  }
  const scan = (cur: Lex): Lex => {
    const c = cur.src[cur.i];
    if (c === undefined || !isSymbolCont(c)) {
      return cur;
    }
    return scan(advance(cur, 1));
  };
  const end = scan(afterColon);
  const name = afterColon.src.slice(afterColon.i, end.i);
  if (RESERVED_WORDS.has(name)) {
    throw new ReservedWordError(name, posFrom(start, end));
  }
  return { tok: { t: 'keyword', name, pos: posFrom(start, end) }, next: end };
};

const readWord = (start: Lex): Read => {
  const scan = (cur: Lex): Lex => {
    const c = cur.src[cur.i];
    if (isWordBoundary(c)) {
      return cur;
    }
    return scan(advance(cur, 1));
  };
  const end = scan(start);
  const word = start.src.slice(start.i, end.i);
  const pos = posFrom(start, end);

  // Reserved words are lexed as dedicated tokens — they cannot be used as
  // symbols, op names, or column names (§4.2).
  if (word === 'true' || word === 'false') {
    return { tok: { t: 'boolean', value: word === 'true', pos }, next: end };
  }
  if (word === 'null') {
    return { tok: { t: 'null', pos }, next: end };
  }

  // §4.2 lookahead: a `-` immediately followed by a digit starts a number;
  // bare `-` (and `-` followed by non-digit) is a symbol. Equivalent
  // post-scan rule: if the word begins with a digit or with `-<digit>`, it
  // is number-shaped and validated as a number; otherwise it is
  // symbol-shaped.
  const c0 = word[0];
  const looksNumeric = isDigit(c0) || (c0 === '-' && word.length >= 2 && isDigit(word[1]));

  if (looksNumeric) {
    if (!NUMBER_RE.test(word)) {
      throw new InvalidNumberError(word, pos);
    }
    return { tok: { t: 'number', value: Number(word.replace(/_/g, '')), pos }, next: end };
  }

  if (!SYMBOL_RE.test(word)) {
    throw new InvalidTokenError(word, pos);
  }

  return { tok: { t: 'symbol', name: word, pos }, next: end };
};

const readToken = (lex: Lex): Read => {
  const c = lex.src[lex.i];
  if (c === undefined) {
    return { tok: null, next: lex };
  }
  if (isWs(c)) {
    return { tok: null, next: advance(lex, 1) };
  }
  if (c === ';') {
    return { tok: null, next: skipComment(lex) };
  }
  const single = SINGLE_CHAR_TOKS[c];
  if (single !== undefined) {
    const next = advance(lex, 1);
    return { tok: { t: single, pos: posFrom(lex, next) }, next } as any; // FIXME
  }
  if (c === '"') {
    return readString(lex);
  }
  if (c === ':') {
    return readKeyword(lex);
  }
  return readWord(lex);
};

type TokenizeResult = { readonly tokens: readonly Tok[]; readonly eof: SourcePos };

const tokenize = (src: string): TokenizeResult => {
  const go = (lex: Lex, acc: readonly Tok[]): { tokens: readonly Tok[]; finalLex: Lex } => {
    if (lex.i >= lex.src.length) {
      return { tokens: acc, finalLex: lex };
    }
    const { tok, next } = readToken(lex);
    return go(next, tok ? [...acc, tok] : acc);
  };
  const { tokens, finalLex } = go(initLex(src), []);
  return {
    tokens,
    eof: { line: finalLex.line, col: finalLex.col, offset: finalLex.i, length: 0 },
  };
};

// Parser (recursive descent over the token stream)

type Cur = { readonly toks: readonly Tok[]; readonly p: number };
type Parsed<T> = { readonly node: T; readonly cur: Cur };

const peek = (cur: Cur): Tok | undefined => cur.toks[cur.p];
const step = (cur: Cur): Cur => ({ toks: cur.toks, p: cur.p + 1 });

// Paren-like describers are derived from `PAREN_CHARS` so the recognising
// char and the diagnostic text don't drift. The mapped-type cast restores
// the discriminant-narrowed shape that `Object.fromEntries` erases.
const PAREN_DESCRIBERS = Object.fromEntries(
  Object.entries(PAREN_CHARS).map(([kind, char]) => [kind, () => `"${char}"`] as const),
) as { [K in keyof typeof PAREN_CHARS]: () => string };

// Lookup table: token kind → describer. The mapped type preserves each
// branch's discriminant narrowing so `tok.value` / `tok.name` access is
// type-checked without manual casts inside each describer.
const TOK_DESCRIBERS: { [K in Tok['t']]: (tok: Extract<Tok, { t: K }>) => string } = {
  ...PAREN_DESCRIBERS,
  string: () => 'string literal',
  null: () => 'reserved word "null"',
  number: (tok) => `number ${tok.value}`,
  boolean: (tok) => `reserved word "${String(tok.value)}"`,
  symbol: (tok) => `symbol "${tok.name}"`,
  keyword: (tok) => `keyword ":${tok.name}"`,
};

const describeTok = (tok: Tok): string => (TOK_DESCRIBERS[tok.t] as (tok: Tok) => string)(tok);

// Closing delim spans the closing token; the list/vector span runs from the
// open delim's offset to one past the close delim.
const spanFromTo = (open: SourcePos, close: SourcePos): SourcePos => ({
  line: open.line,
  col: open.col,
  offset: open.offset,
  length: close.offset + close.length - open.offset,
});

export function parse(src: string): readonly AstNode[] {
  // Defensive: a non-string (e.g. `undefined` from a tool call whose
  // args are still streaming) must fail as a structured `ParseError`,
  // not an unstructured `TypeError: lex.src is undefined` that escapes
  // the engine's error contract and tears down React subtrees. Empty /
  // whitespace-only input still flows through the `tokens.length === 0`
  // path below for an accurate `eof` position.
  if (typeof src !== 'string') {
    throw new UnexpectedEofError('expected an expression, got empty input', NO_POS);
  }
  const { tokens, eof } = tokenize(src);

  if (tokens.length === 0) {
    throw new UnexpectedEofError('expected an expression, got empty input', eof);
  }

  // Mutual recursion via const-arrow closures: parseExpr references parseList
  // and parseVector, both of which reference parseExpr. References are
  // resolved at call time, by which point all three are defined.
  const parseList = (cur: Cur, openPos: SourcePos): Parsed<ListNode> => {
    const afterOpen = step(cur);
    const head = peek(afterOpen);
    if (head === undefined) {
      throw new UnclosedListError(openPos);
    }
    if (head.t === 'rparen') {
      throw new EmptyListError(spanFromTo(openPos, head.pos));
    }
    if (head.t !== 'symbol') {
      throw new ListHeadNotSymbolError(describeTok(head), head.pos);
    }

    // Positional and keyword args may interleave freely; `normalizeList`
    // reconstructs `params` from positional-encounter order regardless of
    // where kwargs fall, so no ordering rule is enforced here.
    const collectArgs = (
      c: Cur,
      acc: readonly AstArg[],
    ): { args: readonly AstArg[]; afterClose: Cur; closePos: SourcePos } => {
      const t = peek(c);
      if (t === undefined) {
        throw new UnclosedListError(openPos);
      }
      if (t.t === 'rparen') {
        return { args: acc, afterClose: step(c), closePos: t.pos };
      }
      if (t.t === 'keyword') {
        const afterKey = step(c);
        const valTok = peek(afterKey);
        // A kwarg's value must be a parseable expression. If the next token
        // closes the list, is another keyword, or is missing, the user
        // forgot the value — that's a more useful diagnostic than letting
        // parseExpr surface a generic "stray )"/"bare keyword".
        if (valTok === undefined || valTok.t === 'rparen' || valTok.t === 'keyword') {
          throw new KwargNoValueError(t.name, t.pos);
        }
        const { node: value, cur: afterValue } = parseExpr(afterKey);
        const kwarg: AstArg = { kind: 'kwarg', key: t.name, keyPos: t.pos, value };
        return collectArgs(afterValue, [...acc, kwarg]);
      }
      const { node, cur: afterValue } = parseExpr(c);
      return collectArgs(afterValue, [...acc, { kind: 'positional', value: node }]);
    };

    const afterHead = step(afterOpen);
    const { args, afterClose, closePos } = collectArgs(afterHead, []);
    return {
      node: {
        kind: 'list',
        op: head.name,
        opPos: head.pos,
        args,
        pos: spanFromTo(openPos, closePos),
      },
      cur: afterClose,
    };
  };

  const parseVector = (cur: Cur, openPos: SourcePos): Parsed<VectorNode> => {
    const collectElems = (
      c: Cur,
      acc: readonly AstNode[],
    ): { elems: readonly AstNode[]; afterClose: Cur; closePos: SourcePos } => {
      const t = peek(c);
      if (t === undefined) {
        throw new UnclosedVectorError(openPos);
      }
      if (t.t === 'rbracket') {
        return { elems: acc, afterClose: step(c), closePos: t.pos };
      }
      // §4.4: "Keywords are not values." Vectors are containers of values,
      // so a keyword token inside a vector is an error — even though §4.1's
      // example informally mentions a keyword "treated as a name", §4.4 is
      // the normative rule and the AST has no keyword node kind.
      if (t.t === 'keyword') {
        throw new BareKeywordError(t.name, t.pos);
      }
      const { node, cur: afterElem } = parseExpr(c);
      return collectElems(afterElem, [...acc, node]);
    };

    const { elems, afterClose, closePos } = collectElems(step(cur), []);
    return {
      node: { kind: 'vector', elements: elems, pos: spanFromTo(openPos, closePos) },
      cur: afterClose,
    };
  };

  const parseExpr = (cur: Cur): Parsed<AstNode> => {
    const tok = peek(cur);
    if (tok === undefined) {
      throw new UnexpectedEofError('unexpected end of input', eof);
    }
    if (tok.t === 'lparen') {
      return parseList(cur, tok.pos);
    }
    if (tok.t === 'lbracket') {
      return parseVector(cur, tok.pos);
    }
    if (tok.t === 'symbol') {
      return {
        node: { kind: 'symbol', name: tok.name, pos: tok.pos },
        cur: step(cur),
      };
    }
    if (tok.t === 'keyword') {
      throw new BareKeywordError(tok.name, tok.pos);
    }
    if (tok.t === 'rparen') {
      throw new StrayRparenError(tok.pos);
    }
    if (tok.t === 'rbracket') {
      throw new StrayRbracketError(tok.pos);
    }
    // Remaining cases: number, string, boolean, null — all literal nodes.
    const value = tok.t === 'null' ? null : (tok as any).value; // FIXME
    return {
      node: { kind: 'literal', value, pos: tok.pos },
      cur: step(cur),
    };
  };

  // A script is one-or-more top-level expressions; the caller evaluates them
  // in order and the value of the last expression is the script's result.
  // Sequencing is a property of the entry point, not an explicit op.
  const collectForms = (cur: Cur, acc: readonly AstNode[]): readonly AstNode[] => {
    if (cur.p >= tokens.length) {
      return acc;
    }
    const { node, cur: afterForm } = parseExpr(cur);
    return collectForms(afterForm, [...acc, node]);
  };

  return collectForms({ toks: tokens, p: 0 }, []);
}
